import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        /**
         * accent — the app's signature CTA (brand green, design-system
         * §2/§3). Card.Actions' single PRIMARY button uses this, not
         * `default` (shadcn's neutral off-white) — it replaces the old
         * `onboardingPrimaryButtonStyle`/`primaryButtonStyle` inline objects.
         */
        /**
         * D2 (07/09/2026): a tinta era `text-axg-bg`, que é o fundo da PÁGINA.
         * No escuro coincide com a tinta certa (#0a0a0b, 10,39:1); no CLARO é o
         * papel #f4f1ea sobre o verde #0e9c6b — **3,11:1**, reprovado em AA para
         * 14px. `--axg-accent-foreground` já existia e já estava certo nos dois
         * temas: 10,39:1 no escuro, 4,84:1 no claro. O token nunca foi o
         * problema; o botão é que não o usava.
         */
        accent: 'bg-axg-accent text-axg-accent-foreground font-semibold hover:bg-axg-accent-hover',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        /**
         * outline — the card's SECONDARY action ("Criar nova planta",
         * "Tentar de novo"): below `accent`, above `nav`. It used to be the one
         * variant still speaking raw shadcn (`border-input` + a flat
         * `bg-background` fill), which rendered a grey box in the middle of the
         * AxiomGO cards. Now it wears the same resting border as the plant
         * pills (`chip`) and the choice rows (`option`) — `axg-border-subtle`
         * over a transparent surface — and reads louder than `option` only
         * through `text-axg-primary`, because it is an ACTION and not a choice.
         * Brand green stays exclusive to `accent`; hover lifts border+surface
         * one step (`axg-border-strong` / `axg-surface-2`), never to green.
         */
        /**
         * (b) do B3-DESTRAVA (07/09/2026) — a borda saiu de
         * `axg-border-subtle` para `axg-border-control`. Medido no navegador,
         * antes: **1,10:1 no escuro e 1,17:1 no claro**. A WCAG 2.1 §1.4.11
         * pede 3:1 para o contorno de um controle, e esta variante tem fundo
         * transparente: a borda é a ÚNICA coisa que diz onde o botão começa.
         * Como `outline` é a CTA secundária de TODA a casca, o produto inteiro
         * estava abaixo do critério — foi a #773 que mediu isso de raspão, ao
         * procurar outra coisa.
         *
         * O `hover:border-axg-border-strong` saiu junto, e não por estética: em
         * repouso a borda passou a 0,42/0,55 de alfa, e `border-strong` é
         * 0,12/0,16 — o hover BAIXARIA o contorno. Sobra o realce de superfície,
         * que é o que o hover deve fazer aqui.
         *
         * Esta variante absorveu a `outlineStrong` (#773), que existia só
         * porque a cerca do KPI impedia mexer aqui. Com a cerca emendada
         * (#776), o remendo morreu: a variante foi APAGADA e os cinco pontos
         * que a usavam voltaram para `outline`. O primitivo cobre.
         */
        outline:
          'rounded-lg border border-axg-border-control bg-transparent text-axg-primary hover:bg-axg-surface-2',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        /**
         * (b) — a tinta saiu de `text-primary` (que é o accent) para
         * `text-axg-link`. Medido: o accent SHINE sobre o papel do tema claro
         * dá **3,11:1**, reprovado em AA para 14px. `--axg-link` já resolvia
         * isso desde a B1, com a razão escrita no próprio token; no escuro ele
         * É o accent, então nada muda lá. É a QUINTA vez nesta sprint que o
         * token estava certo e o componente não o usava.
         */
        link: 'text-axg-link underline-offset-4 hover:underline',
        /**
         * chip — pill-shaped choice control (sector/plant/location pickers).
         * Replaces `chipButtonStyle`. Pressed state is `chipSelected` below —
         * NEVER `accent`: selection lights up in paper-white, brand green is
         * reserved for the card's single primary action (visual grammar
         * 2026-07-29, "escolha ≠ ação").
         */
        chip: 'rounded-full border border-axg-border-subtle bg-axg-surface-1 font-mono text-xs text-axg-primary hover:bg-axg-surface-2',
        /**
         * chipSelected — the "lit" state of `chip`: strong paper-white border
         * + raised surface. Selection is geometry/luminance, not brand green,
         * so a selected chip can never be confused with the accent CTA.
         */
        chipSelected:
          'rounded-full border border-axg-primary/70 bg-axg-surface-2 font-mono text-xs font-semibold text-axg-primary',
        /**
         * option / optionSelected — row-shaped SELECTION control of the
         * choice-card family (ChoiceOption wraps these). An option must read
         * as a selector, never as an action button: selected = lit
         * paper-white border + raised surface (+ the ChoiceOption indicator
         * ring fills). Brand green stays exclusive to `accent`.
         */
        option:
          'justify-start rounded-lg border border-axg-border-subtle bg-transparent text-axg-secondary hover:border-axg-border-strong hover:bg-axg-surface-2',
        optionSelected:
          'justify-start rounded-lg border border-axg-primary/70 bg-axg-surface-2 text-axg-primary',
        /**
         * nav — quiet in-flow navigation (Voltar): ghost weight, secondary
         * text. Deliberately lighter than `outline` so it never competes with
         * the step's accent CTA (visual grammar 2026-07-29).
         */
        nav: 'text-axg-secondary hover:bg-axg-surface-2',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
        /**
         * card-cta — 44px de alvo com MOUSE, 48px com o DEDO (STO-54,
         * 10/09/2026): `.touch-target` sobe para 48px em `pointer: coarse`,
         * que é a régua do produto para celular. O comentário antigo dizia
         * "44px" e ponto — era verdade até a medição no Pixel 7 reprovar.
         * Used by Card.Actions CTAs; mirrors the old inline `minHeight: 44`
         * onboarding/equipment-registration button styles it replaces.
         * `.touch-target` (src/styles/index.css) is the SAME class every
         * other touch-target consumer in the app uses — not a new one-off.
         */
        'card-cta': 'touch-target px-4 py-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
